import { useCallback, useRef, useState } from "react";
import type { ApplyBrandOptions, ApplyBrandResult, BrandSpec } from "../../../types";
import {
  applyBrandToSlide, applyBrandToDeck, captureDeckSnapshot, moveSlidesToMaster, openBrandedCopy, restoreDeckSnapshot,
  type DeckSnapshot,
} from "../services/brandApplier";
import { logError, scrubMessage } from "../../../../../telemetry";

export type ApplyState =
  | { status: "idle" }
  | { status: "applying"; message: string; current: number; total: number; stage: string }
  | { status: "done"; result: ApplyBrandResult }
  /** `kind` picks the BRD EC-27 / EC-28 / EC-29 wording; "generic" shows the raw message. */
  | { status: "error"; message: string; kind: "readonly" | "connection" | "interrupted" | "generic"; slide?: number; total?: number }
  /** Cancelled by the user (FR-58). `rolledBack` is false when nothing needed undoing or it couldn't be undone. */
  | { status: "cancelled"; rolledBack: boolean };

/** connectorSystemName tag for Deckorate apply-brand telemetry. */
const APPLY_BRAND_CONNECTOR = "deckorate";

/** Report an apply-brand failure through the shared telemetry pipeline (PII-scrubbed). */
function logApplyBrandError(reason: string, type: string, message: string): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: APPLY_BRAND_CONNECTOR, reason },
  );
}

/** Which stage the applier's progress message belongs to (BRD FR-57: colours, fonts, text styles). */
export function stageFromMessage(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("table") || m.includes("bullet")) return "text styles";
  if (m.includes("font")) return "fonts";
  if (m.includes("colour") || m.includes("color")) return "colours";
  return "colours";
}

/** True when the open presentation is read-only (BRD EC-27). */
function isPresentationReadOnly(): boolean {
  try {
    return Office?.context?.document?.mode === Office.DocumentMode.ReadOnly;
  } catch (error) {
    logApplyBrandError("brand-apply-document-mode", "ApplyBrandError", error instanceof Error ? error.message : "mode unavailable");
    return false;
  }
}

/** What happens around the apply itself (BRD FR-25, FR-53). */
export interface ApplyExtras {
  /** "copy" opens the branded deck in a new window and leaves the original untouched. */
  readonly destination?: "update" | "copy";
  /** Moves every slide onto this master after the apply (Remove other masters). */
  readonly moveToMaster?: string;
}

/** Pre-flight checks that block an apply before anything is touched (EC-27, EC-28). */
function getPreflightErrorState(): ApplyState | null {
  if (isPresentationReadOnly()) {
    return { status: "error", kind: "readonly", message: "read-only" };
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { status: "error", kind: "connection", message: "offline" };
  }
  return null;
}

/** BR-09: a cancelled apply puts the presentation back the way it was. */
async function resolveCancelledState(result: ApplyBrandResult, snapshot: DeckSnapshot | null): Promise<ApplyState> {
  const rolledBack = snapshot ? await restoreDeckSnapshot(snapshot) : false;
  return { status: "cancelled", rolledBack: result.slidesProcessed === 0 || rolledBack };
}

/** Moves every slide onto the chosen master after a successful apply (Remove other masters). */
async function moveResultToMaster(result: ApplyBrandResult, masterName: string): Promise<void> {
  try {
    const moved = await moveSlidesToMaster(masterName);
    if (moved > 0) result.actions.push(`Moved ${moved} slide(s) onto ${masterName}`);
  } catch (e) {
    logApplyBrandError("brand-apply-move-masters", "ApplyBrandError", e instanceof Error ? e.message : "move failed");
    result.errors.push("Couldn't move slides onto the chosen slide master.");
  }
}

/** FR-61: opens the branded deck in its own window and restores the original in this one. */
async function openCopyAndRestore(snapshot: DeckSnapshot | null): Promise<ApplyState | null> {
  const opened = await openBrandedCopy();
  if (snapshot) await restoreDeckSnapshot(snapshot);
  if (!opened) {
    return { status: "error", kind: "generic", message: "Couldn't open the branded copy in a new window." };
  }
  return null;
}

/** Everything that happens after a successful (non-cancelled) apply (BRD FR-25, FR-53, FR-61). */
async function resolveSuccessState(
  result: ApplyBrandResult,
  extras: ApplyExtras,
  snapshot: DeckSnapshot | null,
): Promise<ApplyState> {
  if (extras.moveToMaster) {
    await moveResultToMaster(result, extras.moveToMaster);
  }
  if (extras.destination === "copy") {
    const errorState = await openCopyAndRestore(snapshot);
    if (errorState) return errorState;
  }
  return { status: "done", result };
}

/** Rolls back the snapshot and builds the error state after a failed apply. */
async function resolveErrorState(
  error: unknown,
  scope: ApplyBrandOptions["scope"] | undefined,
  snapshot: DeckSnapshot | null,
  lastSlide: { current: number; total: number },
): Promise<ApplyState> {
  const message = error instanceof Error ? error.message : "Apply brand failed";
  logApplyBrandError(`brand-apply-${scope ?? "slide"}`, "ApplyBrandError", message);
  const rolledBack = snapshot ? await restoreDeckSnapshot(snapshot) : false;
  return {
    status: "error",
    kind: rolledBack ? "interrupted" : "generic",
    message,
    slide: lastSlide.current + 1,
    total: lastSlide.total,
  };
}

export function useBrandApply() {
  const [state, setState] = useState<ApplyState>({ status: "idle" });
  // Guards against a second apply starting while one is already in flight
  // (e.g. a fast double-click before the "applying" state disables the
  // button) — without this, both PowerPoint.run batches would race.
  const isApplyingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const lastSlideRef = useRef({ current: 0, total: 0 });

  const apply = useCallback(async (brand: BrandSpec, options: Partial<ApplyBrandOptions>, extras: ApplyExtras = {}) => {
    if (isApplyingRef.current) return;

    // Nothing is touched when the deck can't be edited or the connection is down (EC-27, EC-28).
    const preflightError = getPreflightErrorState();
    if (preflightError) {
      setState(preflightError);
      return;
    }

    isApplyingRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;
    lastSlideRef.current = { current: 0, total: 0 };

    // total starts at 1 (not 0) purely so the initial 0/1 ratio still renders
    // a 0% progress bar immediately; the first onProgress call below replaces
    // it with the real slide count.
    setState({ status: "applying", message: "Starting…", current: 0, total: 1, stage: "colours" });

    const snapshot = await captureDeckSnapshot();

    const opts: Partial<ApplyBrandOptions> = {
      ...options,
      signal: controller.signal,
      onProgress: (message: string, current: number, total: number) => {
        lastSlideRef.current = { current, total };
        setState({ status: "applying", message, current, total, stage: stageFromMessage(message) });
      },
    };

    try {
      const result =
        options.scope === "deck"
          ? await applyBrandToDeck(brand, opts)
          : await applyBrandToSlide(brand, opts);

      setState(
        result.cancelled
          ? await resolveCancelledState(result, snapshot)
          : await resolveSuccessState(result, extras, snapshot),
      );
    } catch (e) {
      setState(await resolveErrorState(e, options.scope, snapshot, lastSlideRef.current));
    } finally {
      isApplyingRef.current = false;
      abortRef.current = null;
    }
  }, []);

  /** Asks the running apply to stop at the next slide boundary (FR-58). */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { state, apply, cancel, reset };
}
